/* Magic Hinge 4. Native scrolling, lazy compressed frames, bounded decoded cache. */
(() => {
  'use strict';
  if (window.TopseatMagic4) { window.TopseatMagic4.init(); return; }
  const selector = '[data-ts-magic-4]';
  const instances = new WeakMap();
  const clamp = (n, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, n));
  const smooth = (n) => { const t = clamp(n); return t * t * (3 - 2 * t); };
  const dispose = (image) => { if (image?.close) image.close(); };

  class MagicSequence {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('.ts-magic__stage');
      this.layout = root.querySelector('.ts-magic__layout');
      this.visual = root.querySelector('.ts-magic__frame');
      this.canvas = root.querySelector('[data-ts-canvas]');
      this.poster = root.querySelector('[data-ts-poster]');
      this.captions = [...root.querySelectorAll('[data-ts-caption]')];
      try { this.sources = JSON.parse(root.querySelector('[data-ts-frames]').textContent); }
      catch (_) { return; }
      if (!this.canvas || !this.visual || !this.poster || !this.sources.mobile?.length ||
          this.sources.desktop?.length !== this.sources.mobile.length) return;
      this.context = this.canvas.getContext('2d', { alpha: true });
      if (!this.context) return;
      this.count = this.sources.mobile.length;
      this.header = document.getElementById('sticky-header-content');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.shortScreen = matchMedia('(max-height: 520px) and (orientation: landscape)');
      this.events = new AbortController();
      this.blobs = new Map();
      this.bitmaps = new Map();
      this.requests = new Map();
      this.decoding = new Set();
      this.bad = new Set();
      this.generation = 0;
      this.raf = 0;
      this.measureRaf = 0;
      this.timer = 0;
      this.wanted = 0;
      this.drawn = -1;
      this.progress = 0;
      this.stamp = 0;
      this.near = false;
      this.dead = false;
      this.failed = false;
      this.enabled = false;
      this.variant = '';
      this.tick = this.tick.bind(this);
      this.schedule = this.schedule.bind(this);
      this.requestMeasure = this.requestMeasure.bind(this);
      this.listen(window, 'scroll', this.schedule, { passive: true });
      this.listen(window, 'resize', this.requestMeasure, { passive: true });
      this.listen(window, 'pageshow', () => { this.requestMeasure(); this.schedule(); });
      this.listen(this.motion, 'change', () => this.configure());
      this.listen(this.shortScreen, 'change', () => this.configure());
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.stop();
        else { this.stamp = 0; this.pump(); this.schedule(); }
      });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.requestMeasure);
        this.resizeObserver.observe(this.layout);
        if (this.header) this.resizeObserver.observe(this.header);
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.near = entries.some((entry) => entry.isIntersecting);
          if (this.near) { this.start(); this.schedule(); }
          else this.stop();
        }, { rootMargin: '0px' });
        this.observer.observe(this.visual);
      } else this.near = true;
      this.configure();
    }

    listen(target, name, listener, options = {}) {
      target.addEventListener(name, listener, { ...options, signal: this.events.signal });
    }

    configure() {
      if (this.dead) return;
      this.enabled = !this.motion.matches && !this.shortScreen.matches && !this.failed;
      this.root.classList.toggle('is-motion', this.enabled);
      if (!this.enabled) {
        this.root.classList.remove('is-pinned', 'is-ready');
        this.root.dataset.layout = 'static';
        this.stop();
        this.release();
        this.showPoster(this.count - 1);
        return;
      }
      this.measure();
      this.start();
      this.schedule();
    }

    showPoster(index) {
      this.poster.srcset = `${this.sources.mobile[index]} 960w, ${this.sources.desktop[index]} 1920w`;
      this.poster.src = this.sources.mobile[index];
    }

    requestMeasure() {
      if (this.dead || this.measureRaf) return;
      this.measureRaf = requestAnimationFrame(() => {
        this.measureRaf = 0;
        if (!this.enabled) return;
        this.measure(); this.start(); this.schedule();
      });
    }

    measure() {
      const width = window.innerWidth;
      const height = window.innerHeight;
      this.top = Math.min(this.header?.offsetHeight || (width < 960 ? 68 : 80), height * .25);
      this.root.style.setProperty('--tsm-header', `${Math.round(this.top)}px`);
      const contentHeight = Math.ceil(this.layout.getBoundingClientRect().height);
      const available = height - this.top - 12;
      // A short stage leaves a visible spacer below it, on landscape desktops
      // as well as portrait tablets. Keep those layouts in normal document flow.
      // The phone rule and all existing portrait decisions remain unchanged.
      const shortStage = width >= 600 && contentHeight < available * .84;
      this.pinned = !shortStage && contentHeight <= available;
      this.landscapeFlow = shortStage && width > height;
      const setting = Number(width < 960 ? this.root.dataset.mobileDistance : this.root.dataset.distance);
      const ratio = Number.isFinite(setting) && setting > 0 ? clamp(setting / 100, .8, 2) : 1.2;
      this.travel = this.pinned ? Math.round(clamp(contentHeight * ratio, 360, 1050)) : 0;
      this.root.style.setProperty('--tsm-stage-height', `${contentHeight}px`);
      this.root.style.setProperty('--tsm-travel', `${this.travel}px`);
      this.root.classList.toggle('is-pinned', this.pinned);
      this.root.dataset.layout = this.pinned ? 'pinned' : 'flow';

      const box = this.visual.getBoundingClientRect();
      const pixelWidth = box.width * Math.min(window.devicePixelRatio || 1, 2);
      const sourceWidthNeeded = pixelWidth * Math.max(1, (16 / 9) / (box.width / Math.max(1, box.height)));
      const variant = sourceWidthNeeded <= 960 ? 'mobile' : 'desktop';
      if (variant !== this.variant) {
        this.release(); this.variant = variant;
        this.urls = this.sources[variant];
        this.root.classList.remove('is-ready');
        this.showPoster(this.wanted);
      }
      // Cropping transparent sides must not upscale beyond the source pixels.
      const maxWidth = (variant === 'mobile' ? 960 : 1920) *
        Math.min(1, (box.width / Math.max(1, box.height)) / (16 / 9));
      const renderWidth = Math.max(1, Math.round(Math.min(pixelWidth, maxWidth)));
      const renderHeight = Math.max(1, Math.round(renderWidth * box.height / Math.max(1, box.width)));
      if (this.canvas.width !== renderWidth || this.canvas.height !== renderHeight) {
        this.canvas.width = renderWidth; this.canvas.height = renderHeight;
        this.drawn = -1;
        this.root.classList.remove('is-ready');
        this.paintNearest();
      }
    }

    start() {
      if (!this.enabled || !this.near || this.dead || !this.urls || document.hidden) return;
      if (!this.timer && this.drawn < 0) {
        this.timer = setTimeout(() => {
          this.timer = 0;
          if (!this.dead && this.enabled && this.drawn < 0) this.fail();
        }, 15000);
      }
      this.pump(); this.decode();
    }

    // Fetch only the nearby frames when the animation is visible, so below-fold
    // animation assets do not compete with the homepage hero on mobile.
    // Keep at most eight decoded frames, plus two in-flight decodes.
    pump() {
      if (!this.enabled || !this.near || this.dead || document.hidden || !this.urls) return;
      const candidates = this.urls.map((_, i) => i)
        .filter((i) => Math.abs(i - this.wanted) <= 8 &&
          !this.blobs.has(i) && !this.requests.has(i) && !this.bad.has(i))
        .sort((a, b) => Math.abs(a - this.wanted) - Math.abs(b - this.wanted));
      while (this.requests.size < 4 && candidates.length) this.fetchFrame(candidates.shift());
    }

    async fetchFrame(index) {
      const generation = this.generation;
      const controller = new AbortController();
      this.requests.set(index, controller);
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(this.urls[index], { signal: controller.signal, cache: 'force-cache', priority: 'low' });
        if (!response.ok) throw new Error('Frame unavailable');
        const blob = await response.blob();
        if (this.dead || generation !== this.generation) return;
        this.blobs.set(index, blob);
      } catch (_) {
        if (!this.dead && generation === this.generation) this.bad.add(index);
      } finally {
        clearTimeout(timeout);
        if (!this.dead && generation === this.generation) {
          this.requests.delete(index);
          this.decode(); this.pump();
        }
      }
    }

    decode() {
      if (!this.enabled || !this.near || this.dead || document.hidden) return;
      const candidates = [...this.blobs.keys()]
        .filter((i) => !this.bitmaps.has(i) && !this.decoding.has(i) && !this.bad.has(i))
        .sort((a, b) => Math.abs(a - this.wanted) - Math.abs(b - this.wanted));
      for (const index of candidates) {
        if (this.decoding.size >= 2) break;
        if (Math.abs(index - this.wanted) > 2 && this.bitmaps.size) break;
        this.decodeFrame(index);
      }
    }

    async decodeFrame(index) {
      const generation = this.generation;
      this.decoding.add(index);
      let image;
      try {
        const blob = this.blobs.get(index);
        if (typeof createImageBitmap === 'function') {
          try { image = await createImageBitmap(blob); } catch (_) { /* Image fallback below. */ }
        }
        if (!image) image = await this.decodeImage(blob);
        if (this.dead || generation !== this.generation) { dispose(image); return; }
        this.bitmaps.set(index, image);
        this.trim();
        this.paintNearest();
      } catch (_) {
        if (!this.dead && generation === this.generation) this.bad.add(index);
      } finally {
        if (!this.dead && generation === this.generation) {
          this.decoding.delete(index); this.decode();
        }
      }
    }

    decodeImage(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const image = new Image();
        const finish = (ok) => {
          clearTimeout(timeout); image.onload = image.onerror = null;
          URL.revokeObjectURL(url);
          if (ok) resolve(image); else reject(new Error('Frame decode failed'));
        };
        const timeout = setTimeout(() => finish(false), 10000);
        image.onload = () => finish(true);
        image.onerror = () => finish(false);
        image.src = url;
      });
    }

    trim() {
      const farthest = [...this.bitmaps.keys()].sort((a, b) => Math.abs(b - this.wanted) - Math.abs(a - this.wanted));
      while (this.bitmaps.size > 8) {
        const index = farthest.shift();
        dispose(this.bitmaps.get(index)); this.bitmaps.delete(index);
      }
    }

    paintNearest() {
      if (!this.enabled || this.dead || document.hidden || !this.bitmaps.size) return;
      const index = [...this.bitmaps.keys()].sort((a, b) => Math.abs(a - this.wanted) - Math.abs(b - this.wanted))[0];
      if (index === this.drawn) return;
      const image = this.bitmaps.get(index);
      const width = image.width || image.naturalWidth;
      const height = image.height || image.naturalHeight;
      const ratio = this.canvas.width / this.canvas.height;
      // Same center-bottom crop as the responsive poster.
      const sw = Math.min(width, height * ratio);
      const sh = Math.min(height, width / ratio);
      try {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.context.drawImage(image, (width - sw) / 2, height - sh, sw, sh, 0, 0, this.canvas.width, this.canvas.height);
      } catch (_) { this.fail(); return; }
      this.drawn = index;
      clearTimeout(this.timer); this.timer = 0;
      this.root.classList.add('is-ready');
      this.root.dataset.frame = String(index + 1);
      // Black Pro Elite: the camera approaches before the seat separates.
      // Caption changes follow the frame actually drawn, including slow loads.
      const active = index < 32 ? 0 : index < 50 ? 1 : 2;
      this.captions.forEach((caption, i) => caption.classList.toggle('is-active', i === active));
      this.root.dataset.scene = ['connected', 'lifting', 'detached'][active];
    }

    schedule() {
      if (this.raf || !this.enabled || this.dead || document.hidden) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    tick(stamp) {
      this.raf = 0;
      if (!this.enabled || this.dead || document.hidden) return;
      const rect = this.root.getBoundingClientRect();
      const view = window.innerHeight;
      if (rect.bottom < 0 || rect.top > view) { this.stamp = 0; return; }
      let target;
      if (this.pinned) target = clamp((this.top - rect.top) / Math.max(1, this.travel));
      else {
        const sectionTop = rect.top + window.scrollY;
        const visual = this.visual.getBoundingClientRect();
        // On a short landscape layout, wait until the hinge at the bottom of
        // the image is visible. Preserve the tested portrait/mobile timeline.
        const start = this.landscapeFlow
          ? Math.max(0, visual.top + window.scrollY + visual.height - view + 24)
          : Math.max(0, sectionTop - view * .65);
        const end = Math.max(start + 240, visual.top + window.scrollY - this.top + visual.height * .2);
        target = clamp((window.scrollY - start) / (end - start));
      }
      const dt = this.stamp ? clamp(stamp - this.stamp, 1, 64) : 16.67;
      this.stamp = stamp;
      this.progress += (target - this.progress) * (1 - Math.exp(-dt / 65));
      if (Math.abs(target - this.progress) < .0005) this.progress = target;
      this.wanted = Math.round(smooth((this.progress - .04) / .88) * (this.count - 1));
      this.paintNearest(); this.decode(); this.pump();
      if (this.progress !== target) this.schedule();
    }

    stop() { cancelAnimationFrame(this.raf); this.raf = 0; this.stamp = 0; }

    release() {
      this.generation++;
      for (const controller of this.requests.values()) controller.abort();
      for (const image of this.bitmaps.values()) dispose(image);
      this.requests.clear(); this.blobs.clear(); this.bitmaps.clear(); this.decoding.clear(); this.bad.clear();
      clearTimeout(this.timer); this.timer = 0; this.drawn = -1;
    }

    fail() { this.failed = true; this.configure(); }

    destroy() {
      this.dead = true;
      this.events?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
      this.stop(); cancelAnimationFrame(this.measureRaf);
      if (this.requests) this.release();
    }
  }

  const roots = (scope) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const init = (scope = document) => roots(scope).forEach((root) => {
    if (!instances.has(root)) instances.set(root, new MagicSequence(root));
  });
  const destroy = (scope) => roots(scope).forEach((root) => {
    instances.get(root)?.destroy(); instances.delete(root);
  });
  window.TopseatMagic4 = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
